import { Link, RichTextEditor, getTaskListExtension } from '@mantine/tiptap';
import type { AnyExtension, Editor } from '@tiptap/core';
import { AbeAskControl } from './AbeAskControl';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import TaskItem from '@tiptap/extension-task-item';
import TipTapTaskList from '@tiptap/extension-task-list';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { useEditor } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';

const DEFAULT_COLORS = [
  '#25262b',
  '#868e96',
  '#fa5252',
  '#e64980',
  '#be4bdb',
  '#7950f2',
  '#4c6ef5',
  '#228be6',
  '#15aabf',
  '#12b886',
  '#40c057',
  '#82c91e',
  '#fab005',
  '#fd7e14'
];

// Stable default so editorExtensions isn't rebuilt (and the editor re-created) on every render
const NO_EXTENSIONS: AnyExtension[] = [];

export interface TiptapProps {
  id: string;
  value: string;
  setValue: (nextValue: string) => void;
  placeholder?: string;
  variant?: 'default' | 'subtle';
  withTypographyStyles?: boolean;
  stickyToolbar?: boolean;
  stickyOffset?: string | number;
  withSourceCodeMode?: boolean;
  withTasks?: boolean;
  withBubbleMenu?: boolean;
  withAbeAskButton?: boolean;
  bubbleMenuControls?: ReactNode;
  renderCustomControls?: (editor: Editor | null) => ReactNode;
  extensions?: AnyExtension[];
  disabled?: boolean;
  className?: string;
}

export function AppWysiwygEditor({
  id,
  value,
  setValue,
  placeholder,
  variant = 'default',
  withTypographyStyles = true,
  stickyToolbar = false,
  stickyOffset,
  withSourceCodeMode = true,
  withTasks = false,
  withBubbleMenu = false,
  withAbeAskButton = false,
  bubbleMenuControls,
  renderCustomControls,
  extensions = NO_EXTENSIONS,
  disabled = false,
  className
}: TiptapProps) {
  const [isSourceCodeModeActive, setIsSourceCodeModeActive] = useState(false);

  const editorExtensions = useMemo(() => {
    const builtInExtensions: AnyExtension[] = [
      StarterKit.configure({ link: false }),
      Link,
      TextStyle,
      Color,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Highlight,
      Subscript,
      Superscript
      //Italic
    ];

    if (placeholder) {
      builtInExtensions.push(Placeholder.configure({ placeholder }));
    }

    if (withTasks) {
      builtInExtensions.push(getTaskListExtension(TipTapTaskList));
      builtInExtensions.push(
        TaskItem.configure({
          nested: true
        })
      );
    }

    return [...builtInExtensions, ...extensions];
  }, [extensions, placeholder, withTasks]);

  const editor = useEditor({
    shouldRerenderOnTransaction: true,
    extensions: editorExtensions,
    content: value,
    editable: !disabled,
    onUpdate: ({ editor: activeEditor }) => {
      // Tiptap always keeps a trailing empty paragraph node, so getHTML() on an empty
      // doc returns "<p></p>" instead of "" — normalize that back to an empty string.
      if (activeEditor.isDestroyed) return;
      setValue(activeEditor.isEmpty ? '' : activeEditor.getHTML());
    }
  });

  useEffect(() => {
    // A destroyed editor has no schema, so getHTML() would throw
    if (!editor || editor.isDestroyed) {
      return;
    }

    const currentHtml = editor.isEmpty ? '' : editor.getHTML();
    if (value !== currentHtml) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [editor, value]);

  useEffect(() => {
    if (!editor) {
      return;
    }

    editor.setEditable(!disabled);
  }, [disabled, editor]);

  return (
    <RichTextEditor
      id={id}
      editor={editor}
      variant={variant}
      withTypographyStyles={withTypographyStyles}
      onSourceCodeTextSwitch={setIsSourceCodeModeActive}
      className={className}
    >
      <RichTextEditor.Toolbar sticky={stickyToolbar} stickyOffset={stickyOffset}>
        {withSourceCodeMode && (
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.SourceCode />
          </RichTextEditor.ControlsGroup>
        )}

        {!isSourceCodeModeActive && (
          <>
            <RichTextEditor.ControlsGroup>
              <RichTextEditor.Bold />
              <RichTextEditor.Italic />
              <RichTextEditor.Underline />
              <RichTextEditor.Strikethrough />
              <RichTextEditor.ClearFormatting />
              <RichTextEditor.Highlight />
            </RichTextEditor.ControlsGroup>

            <RichTextEditor.ControlsGroup>
              <RichTextEditor.H1 />
              <RichTextEditor.H2 />
              <RichTextEditor.H3 />
              <RichTextEditor.H4 />
            </RichTextEditor.ControlsGroup>

            <RichTextEditor.ControlsGroup>
              <RichTextEditor.BulletList />
              <RichTextEditor.OrderedList />
            </RichTextEditor.ControlsGroup>

            {withTasks && (
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.TaskList />
                <RichTextEditor.TaskListLift />
                <RichTextEditor.TaskListSink />
              </RichTextEditor.ControlsGroup>
            )}

            <RichTextEditor.ControlsGroup>
              <RichTextEditor.Link />
              <RichTextEditor.Unlink />
            </RichTextEditor.ControlsGroup>

            <RichTextEditor.ControlsGroup>
              <RichTextEditor.AlignLeft />
              <RichTextEditor.AlignCenter />
              <RichTextEditor.AlignJustify />
              <RichTextEditor.AlignRight />
            </RichTextEditor.ControlsGroup>

            <RichTextEditor.ControlsGroup>
              <RichTextEditor.ColorPicker colors={DEFAULT_COLORS} />
              <RichTextEditor.UnsetColor />
            </RichTextEditor.ControlsGroup>

            <RichTextEditor.ControlsGroup>
              <RichTextEditor.Undo />
              <RichTextEditor.Redo />
            </RichTextEditor.ControlsGroup>

            {renderCustomControls && (
              <RichTextEditor.ControlsGroup>
                {renderCustomControls(editor)}
              </RichTextEditor.ControlsGroup>
            )}

            {withAbeAskButton && (
              <RichTextEditor.ControlsGroup>
                <AbeAskControl editor={editor} disabled={disabled} />
              </RichTextEditor.ControlsGroup>
            )}
          </>
        )}
      </RichTextEditor.Toolbar>

      {withBubbleMenu && editor && !isSourceCodeModeActive && (
        <BubbleMenu editor={editor}>
          {bubbleMenuControls || (
            <RichTextEditor.ControlsGroup>
              <RichTextEditor.Bold />
              <RichTextEditor.Italic />
              <RichTextEditor.Link />
            </RichTextEditor.ControlsGroup>
          )}
        </BubbleMenu>
      )}

      <RichTextEditor.Content />
    </RichTextEditor>
  );
}
